// src/utils/colorUtils.js

/**
 * Converts HEX color to RGB.
 * @param {string} hex - HEX color string.
 * @returns {object} RGB values.
 */
export const hexToRgb = (hex) => {
  if (typeof hex !== 'string') {
    console.error(`hexToRgb: Expected a string, but received ${typeof hex}`);
    return { r: 0, g: 0, b: 0 };
  }

  // Remove the hash symbol if present
  const sanitizedHex = hex.replace('#', '');

  // Ensure it's a valid 6-character HEX
  if (sanitizedHex.length !== 6) {
    console.error(`hexToRgb: Invalid HEX color format: ${hex}`);
    return { r: 0, g: 0, b: 0 };
  }

  const bigint = parseInt(sanitizedHex, 16);
  if (isNaN(bigint)) {
    console.error(`hexToRgb: Unable to parse HEX color: ${hex}`);
    return { r: 0, g: 0, b: 0 };
  }

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};

/**
 * Converts RGB values to HEX format.
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} HEX color string.
 */
export const rgbToHex = (r, g, b) => {
  const toHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Generates a linear gradient between two HEX colors.
 * @param {string} startColor - Starting HEX color.
 * @param {string} endColor - Ending HEX color.
 * @param {number} numberOfBars - Total number of bars.
 * @returns {array} Array of HEX color strings.
 */
export const generateColorGradient = (startColor, endColor, numberOfBars) => {
  const start = parseInt(startColor.slice(1), 16);
  const end = parseInt(endColor.slice(1), 16);

  const startR = (start >> 16) & 0xFF;
  const startG = (start >> 8) & 0xFF;
  const startB = start & 0xFF;

  const endR = (end >> 16) & 0xFF;
  const endG = (end >> 8) & 0xFF;
  const endB = end & 0xFF;

  const gradient = [];

  for (let i = 0; i < numberOfBars; i++) {
    const ratio = i / (numberOfBars - 1);
    const r = Math.round(startR + ratio * (endR - startR));
    const g = Math.round(startG + ratio * (endG - startG));
    const b = Math.round(startB + ratio * (endB - startB));
    gradient.push(`#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`);
  }

  return gradient;
};
